/**
 * Helper script to seed test data into Firestore
 * Run this from the browser console to populate test data
 */

import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { COLLECTIONS } from '../constants/collections';

export async function seedTestData(landlordId: string) {
  console.log('🌱 Seeding test data for landlordId:', landlordId);

  try {
    // Seed Properties
    const property1 = await addDoc(collection(db, COLLECTIONS.PROPERTIES), {
      landlordId,
      name: 'Sunset Apartments',
      address: '123 Main Street',
      city: 'Springfield',
      province: 'ON',
      postalCode: 'M5H 2N2',
      type: 'Apartment Complex',
      totalUnits: 12,
      occupiedUnits: 11,
      imageUrl: 'https://via.placeholder.com/400x250/0967D2/FFFFFF?text=Sunset+Apartments',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    const property2 = await addDoc(collection(db, COLLECTIONS.PROPERTIES), {
      landlordId,
      name: 'Oak Street Building',
      address: '456 Oak Street',
      city: 'Springfield',
      province: 'ON',
      postalCode: 'M6J 3M5',
      type: 'Multi-Family',
      totalUnits: 8,
      occupiedUnits: 7,
      imageUrl: 'https://via.placeholder.com/400x250/01B574/FFFFFF?text=Oak+Street',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    console.log('✅ Properties created');

    // Seed Tenants
    const tenants = [
      {
        landlordId,
        firstName: 'John',
        lastName: 'Smith',
        email: 'john.smith@example.com',
        phone: '(555) 123-4567',
        propertyId: property1.id,
        unitId: 'unit-101',
        unitNumber: '101',
        propertyAddress: '123 Main Street, Unit 101',
        monthlyRent: 1500,
        leaseStartDate: Timestamp.fromDate(new Date('2024-01-01')),
        leaseEndDate: Timestamp.fromDate(new Date('2025-12-31')),
        status: 'active',
        balance: 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      },
      {
        landlordId,
        firstName: 'Sarah',
        lastName: 'Johnson',
        email: 'sarah.j@example.com',
        phone: '(555) 234-5678',
        propertyId: property1.id,
        unitId: 'unit-204',
        unitNumber: '204',
        propertyAddress: '123 Main Street, Unit 204',
        monthlyRent: 1800,
        leaseStartDate: Timestamp.fromDate(new Date('2024-03-01')),
        leaseEndDate: Timestamp.fromDate(new Date('2026-02-28')),
        status: 'active',
        balance: 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      },
      {
        landlordId,
        firstName: 'Mike',
        lastName: 'Davis',
        email: 'mike.davis@example.com',
        phone: '(555) 345-6789',
        propertyId: property2.id,
        unitId: 'unit-305',
        unitNumber: '305',
        propertyAddress: '456 Oak Street, Unit 305',
        monthlyRent: 1600,
        leaseStartDate: Timestamp.fromDate(new Date('2024-06-01')),
        leaseEndDate: Timestamp.fromDate(new Date('2025-05-31')),
        status: 'active',
        balance: 1600,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      },
      {
        landlordId,
        firstName: 'Emily',
        lastName: 'Chen',
        email: 'emily.chen@example.com',
        phone: '(555) 456-7890',
        propertyId: property1.id,
        unitId: 'unit-102',
        unitNumber: '102',
        propertyAddress: '123 Main Street, Unit 102',
        monthlyRent: 1750,
        leaseStartDate: Timestamp.fromDate(new Date('2023-09-01')),
        leaseEndDate: Timestamp.fromDate(new Date('2025-08-31')),
        status: 'late',
        balance: 3500,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      },
    ];

    const tenantRefs = [];
    for (const tenant of tenants) {
      const ref = await addDoc(collection(db, COLLECTIONS.TENANTS), tenant);
      tenantRefs.push({ id: ref.id, ...tenant });
    }

    console.log('✅ Tenants created:', tenantRefs.length);

    // Seed Payments
    const payments = [
      {
        landlordId,
        tenantId: tenantRefs[0].id,
        tenantName: `${tenantRefs[0].firstName} ${tenantRefs[0].lastName}`,
        propertyAddress: tenantRefs[0].propertyAddress,
        amount: 1500,
        expectedAmount: 1500,
        paymentDate: Timestamp.fromDate(new Date('2025-11-15')),
        dueDate: Timestamp.fromDate(new Date('2025-11-01')),
        status: 'completed',
        method: 'Interac e-Transfer',
        reference: 'INT-2025-11-001',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      },
      {
        landlordId,
        tenantId: tenantRefs[1].id,
        tenantName: `${tenantRefs[1].firstName} ${tenantRefs[1].lastName}`,
        propertyAddress: tenantRefs[1].propertyAddress,
        amount: 1800,
        expectedAmount: 1800,
        paymentDate: Timestamp.fromDate(new Date('2025-11-14')),
        dueDate: Timestamp.fromDate(new Date('2025-11-01')),
        status: 'completed',
        method: 'Bank Transfer',
        reference: 'BNK-2025-11-002',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      },
      {
        landlordId,
        tenantId: tenantRefs[2].id,
        tenantName: `${tenantRefs[2].firstName} ${tenantRefs[2].lastName}`,
        propertyAddress: tenantRefs[2].propertyAddress,
        amount: 1600,
        expectedAmount: 1600,
        paymentDate: Timestamp.fromDate(new Date('2025-11-10')),
        dueDate: Timestamp.fromDate(new Date('2025-11-01')),
        status: 'pending',
        method: 'Pending',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      },
      {
        landlordId,
        tenantId: tenantRefs[3].id,
        tenantName: `${tenantRefs[3].firstName} ${tenantRefs[3].lastName}`,
        propertyAddress: tenantRefs[3].propertyAddress,
        amount: 0,
        expectedAmount: 1750,
        paymentDate: Timestamp.fromDate(new Date('2025-11-05')),
        dueDate: Timestamp.fromDate(new Date('2025-10-01')),
        status: 'late',
        notes: '2 months overdue',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      },
    ];

    for (const payment of payments) {
      await addDoc(collection(db, COLLECTIONS.PAYMENTS), payment);
    }

    console.log('✅ Payments created:', payments.length);
    console.log('🎉 Test data seeded successfully!');
    console.log('📊 Summary:');
    console.log('  - Properties: 2');
    console.log('  - Tenants: 4');
    console.log('  - Payments: 4');

    return { success: true };
  } catch (error) {
    console.error('❌ Error seeding data:', error);
    return { success: false, error };
  }
}

// Export for use in browser console
declare global {
  interface Window {
    seedTestData: typeof seedTestData;
  }
}

if (import.meta.env?.DEV) {
  window.seedTestData = seedTestData;
  // eslint-disable-next-line no-console
  console.log('💡 Dev tools available:');
  // eslint-disable-next-line no-console
  console.log('  - window.seedTestData(landlordId) - Seed test data');
  // eslint-disable-next-line no-console
  console.log('  - window.cleanupTestData(landlordId) - Clean up test data');
  // eslint-disable-next-line no-console
  console.log('  - window.runSetup() - Auto-detect user and seed data');
}
